// LinkedDuplicatesPanel — D7 duplicate_of ticket relationships (separate from parent/child).

import React, { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Copy, Link2, Inbox, XCircle } from 'lucide-react';
import { useDisplayTimezone } from '../../contexts/AppConfigContext';

const formatLinkedAt = (iso, tz) => {
  if (!iso) return '—';
  if (!tz) return '…';
  try {
    return new Date(iso).toLocaleString('en-GB', { timeZone: tz, hour12: false });
  } catch {
    return '—';
  }
};

const StatusPill = ({ statusName }) => {
  if (!statusName) return null;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200">
      {statusName}
    </span>
  );
};

const DuplicateOfBanner = ({
  canonical,
  displayTimezone,
  canCloseAsDuplicate,
  onCloseAsDuplicate,
}) => (
  <div className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4">
    <div className="text-xs uppercase tracking-wider text-amber-800 dark:text-amber-300 mb-1">
      Duplicate of
    </div>
    <div className="flex items-center gap-2 flex-wrap">
      {canonical.isParentDeleted ? (
        <span className="font-medium text-gray-500 dark:text-gray-400 line-through">
          {canonical.ticketNumber} (deleted)
        </span>
      ) : (
        <RouterLink
          to={`/agent/tickets/${canonical.ticketId}`}
          className="font-medium text-amber-900 dark:text-amber-200 hover:underline font-mono"
        >
          {canonical.ticketNumber}
        </RouterLink>
      )}
      <StatusPill statusName={canonical.statusName || canonical.statusCode} />
    </div>
    <div className="mt-1 text-sm text-gray-700 dark:text-gray-300 truncate" title={canonical.subject}>
      {canonical.subject || '—'}
    </div>
    <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
      Linked at {formatLinkedAt(canonical.linkedAt, displayTimezone)}
    </div>
    {canCloseAsDuplicate && (
      <div className="mt-3 flex justify-end">
        <button
          type="button"
          onClick={onCloseAsDuplicate}
          className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50"
        >
          <XCircle className="h-4 w-4 mr-1.5" />
          Close as Duplicate
        </button>
      </div>
    )}
  </div>
);

const DuplicateRow = ({ row, displayTimezone }) => (
  <tr className="border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40">
    <td className="px-3 py-2.5 text-sm">
      <RouterLink
        to={`/agent/tickets/${row.ticketId}`}
        className="font-medium text-amber-700 dark:text-amber-300 hover:underline font-mono"
      >
        {row.ticketNumber}
      </RouterLink>
    </td>
    <td className="px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 max-w-xs truncate" title={row.subject}>
      {row.subject || '—'}
    </td>
    <td className="px-3 py-2.5 text-sm">
      <StatusPill statusName={row.statusName || row.statusCode} />
    </td>
    <td className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
      {formatLinkedAt(row.linkedAt, displayTimezone)}
    </td>
  </tr>
);

const DuplicatesTable = ({ duplicates, displayTimezone }) => (
  <div className="overflow-x-auto -mx-2">
    <table className="w-full">
      <thead>
        <tr className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          <th className="px-3 py-2">Ticket</th>
          <th className="px-3 py-2">Subject</th>
          <th className="px-3 py-2">Status</th>
          <th className="px-3 py-2">Linked</th>
        </tr>
      </thead>
      <tbody>
        {duplicates.map((row) => (
          <DuplicateRow key={row.relationId || row.ticketId} row={row} displayTimezone={displayTimezone} />
        ))}
      </tbody>
    </table>
  </div>
);

const EmptyState = () => (
  <div className="rounded-md border border-dashed border-gray-200 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-900/30 py-6 px-4 text-center">
    <Inbox className="h-7 w-7 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
    <p className="text-sm text-gray-500 dark:text-gray-400">
      No duplicate links recorded for this ticket.
    </p>
  </div>
);

const LinkedDuplicatesPanel = ({
  bundle,
  isLoading,
  canLink,
  onOpenLinkDuplicate,
  canCloseAsDuplicate,
  onCloseAsDuplicate,
}) => {
  const displayTimezone = useDisplayTimezone();

  const view = useMemo(() => {
    if (!bundle) {
      return { duplicateOf: null, duplicates: [], duplicateCount: 0, hasContent: false };
    }
    const duplicateOf = bundle.duplicateOf || null;
    const duplicates = Array.isArray(bundle.duplicates) ? bundle.duplicates : [];
    const duplicateCount = bundle.duplicateCount ?? duplicates.length;
    return {
      duplicateOf,
      duplicates,
      duplicateCount,
      hasContent: Boolean(duplicateOf) || duplicates.length > 0,
    };
  }, [bundle]);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Copy className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Linked Duplicates
          </h3>
          {view.duplicateCount > 0 && !view.duplicateOf && (
            <span className="ml-1 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
              {view.duplicateCount} {view.duplicateCount === 1 ? 'duplicate' : 'duplicates'}
            </span>
          )}
        </div>
        {canLink && (
          <button
            type="button"
            onClick={onOpenLinkDuplicate}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md border border-amber-300 dark:border-amber-700 text-amber-900 dark:text-amber-100 bg-white dark:bg-gray-800 hover:bg-amber-50 dark:hover:bg-amber-900/30"
          >
            <Link2 className="h-4 w-4 mr-1.5" />
            Link as Duplicate
          </button>
        )}
      </div>

      {isLoading && (
        <div className="text-sm text-gray-500 dark:text-gray-400 py-2">Loading duplicate links…</div>
      )}

      {!isLoading && view.duplicateOf && (
        <DuplicateOfBanner
          canonical={view.duplicateOf}
          displayTimezone={displayTimezone}
          canCloseAsDuplicate={canCloseAsDuplicate}
          onCloseAsDuplicate={onCloseAsDuplicate}
        />
      )}

      {!isLoading && !view.duplicateOf && view.duplicates.length > 0 && (
        <DuplicatesTable duplicates={view.duplicates} displayTimezone={displayTimezone} />
      )}

      {!isLoading && !view.hasContent && <EmptyState />}
    </div>
  );
};

export default LinkedDuplicatesPanel;
